import { computed, ref, watch, type ComputedRef, type Ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import { DeleteDashboardFlow } from '../flows/DeleteDashboardFlow'
import { COPY_PREFIX, SaveDashboardFlow } from '../flows/SaveDashboardFlow'
import type { DashboardDefinitionDto } from '../models/api.types'
import type { SaveDashboardOutcome } from '../models/save.types'
import type { DashboardSurfaceState, DashboardWorkingCopy } from '../models/surface.types'
import { buildCustomDashboardRoute } from '../service/dashboardRoute'
import { parseLayout } from '../service/layout/serialize'
import { useDashboardStore } from '../stores/dashboardStore'

import { useDashboardRbac } from './useDashboardRbac'

import { useAccount, useWorkspace } from '@/features/account'

export interface UseDashboardOptions {
  scope?: 'workspace' | 'accountCenter'
}

export interface UseDashboard {
  state: Ref<DashboardSurfaceState>
  error: Ref<unknown>
  working: Ref<DashboardWorkingCopy | null>
  saving: Ref<boolean>
  deleting: Ref<boolean>
  readOnly: ComputedRef<boolean>
  reload: () => Promise<void>
  save: (duplicate?: boolean) => Promise<SaveDashboardOutcome | null>
  remove: () => Promise<'deleted' | 'failed' | 'skipped'>
}

function routeText(value: unknown): string | null {
  if (typeof value === 'string' && value.trim()) return value
  return Array.isArray(value) && typeof value[0] === 'string' ? value[0] : null
}

function copyOf(definition: DashboardDefinitionDto): DashboardWorkingCopy {
  return {
    id: definition.id,
    name: definition.name ?? '',
    urlId: definition.url_id,
    version: definition.version,
    workspaceId: definition.workspace_id,
    rows: parseLayout(definition.sections ?? []),
    templateId: definition.source_template_id,
  }
}

function blank(ownerId: number | string): DashboardWorkingCopy {
  return {
    id: null,
    name: '',
    urlId: null,
    version: null,
    workspaceId: ownerId,
    rows: [],
    templateId: null,
  }
}

export function useDashboard(options: UseDashboardOptions = {}): UseDashboard {
  const scope = options.scope ?? 'workspace'
  const route = useRoute()
  const router = useRouter()
  const account = useAccount()
  const workspace = useWorkspace()
  const store = useDashboardStore()
  const rbac = useDashboardRbac()
  const state = ref<DashboardSurfaceState>('loading')
  const error = ref<unknown>(null)
  const working = ref<DashboardWorkingCopy | null>(null)
  const saving = ref(false)
  const deleting = ref(false)

  const ownerId = computed<number | string | null>(() =>
    scope === 'accountCenter'
      ? (account.accountId.value ?? null)
      : (workspace.workspaceId.value ?? null),
  )
  const readOnly = computed(() => {
    const id = working.value?.id
    return scope === 'workspace' && id != null ? !rbac.canEdit(id) : false
  })

  async function alignRouteContext(): Promise<void> {
    if (scope === 'accountCenter') return
    const routeAccountId = routeText(route.params.account_id)
    if (routeAccountId && String(account.accountId.value ?? '') !== routeAccountId) {
      const target = account.accounts.value.find((item) => String(item.id) === routeAccountId)
      if (target) await account.selectAccount(target)
    }
    const routeWorkspaceId = routeText(route.params.workspace_id)
    if (routeWorkspaceId && String(workspace.workspaceId.value ?? '') !== routeWorkspaceId) {
      const target = workspace.workspaces.value.find((item) => String(item.id) === routeWorkspaceId)
      if (target) await workspace.selectWorkspace(target)
    }
  }

  async function reload(): Promise<void> {
    state.value = 'loading'
    error.value = null
    try {
      await alignRouteContext()
      const owner = ownerId.value
      if (owner == null) {
        working.value = null
        state.value = 'error'
        return
      }

      if (scope === 'accountCenter') await store.loadAccountCenters(owner, true)
      else {
        await store.loadDefinitions({ workspaceId: owner, force: true })
      }

      const url = routeText(route.params.url)
      const duplicateId = routeText(
        scope === 'accountCenter'
          ? route.query.duplicate_account_center_id
          : route.query.duplicate_dashboard_id,
      )
      const catalog = scope === 'accountCenter' ? store.accountCenters : store.definitions
      const source = duplicateId
        ? catalog.find((entry) => String(entry.id) === duplicateId)
        : url
          ? catalog.find((entry) => entry.url_id === url)
          : scope === 'accountCenter'
            ? catalog[0]
            : undefined

      if (source) {
        working.value = copyOf(source)
        if (duplicateId) {
          // v3 `useCustomDashboardStarter.js:150-152` — a working copy carries no identity and is
          // renamed up front, so the user sees what they are about to create before saving. The
          // prefix is `SaveDashboardFlow`'s single spelling, not v3's second one (`Copy ${name}`).
          working.value.name = `${COPY_PREFIX}${working.value.name}`
          working.value.id = null
          working.value.version = null
          working.value.urlId = null
        }
        state.value = 'ready'
        if (!duplicateId && route.name === 'CustomDashboardLegacy') {
          await router.replace(
            buildCustomDashboardRoute(source, {
              accountId: account.accountId.value ?? null,
              workspaceId: workspace.workspaceId.value ?? null,
              workspaceAccountId: workspace.workspace.value?.account_id ?? null,
            }),
          )
        }
        return
      }

      working.value = blank(owner)
      state.value = 'starter'
    } catch (cause) {
      error.value = cause
      working.value = null
      state.value = 'error'
    }
  }

  async function save(duplicate = false): Promise<SaveDashboardOutcome | null> {
    const current = working.value
    const owner = ownerId.value
    if (!current || owner == null || readOnly.value) return null
    saving.value = true
    try {
      const outcome = await new SaveDashboardFlow({
        navigate: async (target) => {
          await router.replace(target)
        },
      }).execute({
        rows: current.rows,
        name: current.name,
        ownerId: owner,
        accountId: account.accountId.value ?? workspace.workspace.value?.account_id ?? null,
        scope,
        id: current.id,
        version: current.version,
        templateId: current.templateId,
        duplicate,
        currentUrlId: current.urlId,
      })
      if (outcome.status === 'saved') {
        working.value = copyOf(outcome.definition)
        state.value = 'ready'
      } else if (outcome.status === 'conflict' && outcome.latest) {
        working.value = copyOf(outcome.latest)
      }
      return outcome
    } finally {
      saving.value = false
    }
  }

  async function remove(): Promise<'deleted' | 'failed' | 'skipped'> {
    const current = working.value
    const owner = ownerId.value
    if (!current?.id || owner == null || readOnly.value) return 'skipped'
    deleting.value = true
    try {
      const outcome = await new DeleteDashboardFlow().execute({
        id: current.id,
        ownerId: owner,
        scope,
        isCurrent: true,
      })
      if (outcome.status === 'failed') return 'failed'
      if (outcome.redirectTo) await router.replace(outcome.redirectTo)
      return 'deleted'
    } finally {
      deleting.value = false
    }
  }

  watch(
    () => [route.name, route.params.url, route.params.workspace_id, account.accountId.value],
    () => void reload(),
    { immediate: true },
  )

  return { state, error, working, saving, deleting, readOnly, reload, save, remove }
}
